import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Badge } from '@/ui/Badge';
import { BuscadorDePersonas } from '@/components/BuscadorDePersonas';
import {
  useBorrarIngreso, useFichaDe, useGuardarIngreso, useIngresos, usePublicarIngreso,
  useSubirFoto, type PersonaDelDirectorio, type TarjetaDeIngreso,
} from './api';

/**
 * NuevoIngreso.tsx — la tarjeta de bienvenida, con vista previa en vivo.
 *
 * ## Quién puede entrar aquí NO es quien el portal creía
 *
 * El servidor usa `newhire_manager` (`celebrations.py:422`): staff **o quien sea
 * responsable de algún área**. El vanilla preguntaba `Auth.isStaff()` a secas,
 * así que a un líder de área que no fuera staff el portal le escondía una
 * función que tenía autorizada.
 *
 * **Eso no se puede arreglar desde aquí**: `/auth/me` devuelve `is_staff` y no
 * dice si la persona lidera un área. Queda escrito en la ficha del módulo con lo
 * que haría falta — un campo más en ese endpoint— porque cambiarlo ya no es un
 * rediseño. Mientras tanto se conserva el criterio de antes, que es el que
 * decide `puedeGestionar` en la pantalla.
 */

interface Borrador {
  full_name: string;
  position: string;
  area: string;
  hire_date: string;
  sede: string;
  leader: string;
  email: string;
  message: string;
  phrase: string;
}

const VACIO: Borrador = {
  full_name: '', position: '', area: '', hire_date: '',
  sede: '', leader: '', email: '', message: '', phrase: '',
};

export function NuevoIngreso({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  const [pestana, setPestana] = useState<'crear' | 'lista'>('crear');
  const [campos, setCampos] = useState<Borrador>(VACIO);
  const [persona, setPersona] = useState<PersonaDelDirectorio | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [fotoClave, setFotoClave] = useState<string | null>(null);
  const [alFeed, setAlFeed] = useState(true);
  const [alCorreo, setAlCorreo] = useState(true);
  const [programada, setProgramada] = useState('');

  const ficha = useFichaDe(persona?.id ?? null);
  const subirFoto = useSubirFoto();
  const guardar = useGuardarIngreso();

  // Al vincular a alguien del maestro, sus datos se traen y sus campos se
  // bloquean: no tiene sentido reescribir a mano lo que ya está registrado.
  useEffect(() => {
    if (!ficha.data) return;
    setCampos((c) => ({
      ...c,
      position: ficha.data.position || '',
      area: ficha.data.area || '',
      hire_date: ficha.data.hire_date || '',
      sede: ficha.data.sede || '',
      email: ficha.data.email || '',
    }));
  }, [ficha.data]);

  useEffect(() => {
    if (persona) setCampos((c) => ({ ...c, full_name: persona.full_name }));
  }, [persona]);

  // La vista previa usa `URL.createObjectURL`, que reserva memoria hasta que se
  // revoca. El vanilla no lo revocaba nunca: abrir el estudio y cambiar de foto
  // diez veces dejaba diez imágenes retenidas hasta recargar la página.
  useEffect(() => () => { if (fotoUrl?.startsWith('blob:')) URL.revokeObjectURL(fotoUrl); }, [fotoUrl]);

  const bloqueados = persona != null;

  function limpiar() {
    setPestana('crear'); setCampos(VACIO); setPersona(null);
    setFotoUrl(null); setFotoClave(null);
    setAlFeed(true); setAlCorreo(true); setProgramada('');
    guardar.reset(); subirFoto.reset();
  }
  function cerrar() { limpiar(); alCerrar(); }

  const poner = (k: keyof Borrador) => (e: { target: { value: string } }) =>
    setCampos((c) => ({ ...c, [k]: e.target.value }));

  function elegirFoto(archivo: File | undefined) {
    if (!archivo) return;
    setFotoUrl(URL.createObjectURL(archivo));
    subirFoto.mutate(archivo, {
      onSuccess: (r) => setFotoClave(r.photo_key),
      onError: (e: unknown) =>
        Utils.toast((e as Error)?.message || 'No se pudo subir la foto', 'error'),
    });
  }

  function enviar(accion: 'draft' | 'schedule' | 'publish') {
    if (!campos.full_name.trim()) { Utils.toast('El colaborador es obligatorio', 'error'); return; }
    if (accion === 'schedule' && !programada) {
      Utils.toast('Indica la fecha y hora de publicación', 'error'); return;
    }
    guardar.mutate(
      {
        full_name: campos.full_name.trim(),
        position: campos.position.trim(),
        area: campos.area.trim(),
        hire_date: campos.hire_date || null,
        sede: campos.sede.trim(),
        leader: campos.leader.trim(),
        email: campos.email.trim() || null,
        message: campos.message.trim() || null,
        phrase: campos.phrase.trim() || null,
        photo_key: fotoClave,
        user_id: persona?.id ?? null,
        action: accion,
        publish_feed: alFeed,
        send_email: alCorreo,
        scheduled_at: programada ? new Date(programada).toISOString() : null,
      },
      {
        onSuccess: () => {
          const dicho = { draft: 'Borrador guardado', schedule: 'Publicación programada', publish: 'Publicado en el feed' };
          Utils.toast(dicho[accion], 'success');
          if (accion === 'publish') { Utils.confetti?.({ count: 70 }); cerrar(); }
          else setPestana('lista');
        },
        onError: (e: unknown) => Utils.toast((e as Error)?.message || 'No se pudo guardar', 'error'),
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Nuevo Ingreso"
      ancho="max-w-4xl"
      pie={
        pestana === 'crear' ? (
          <>
            <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
            <Button variant="secondary" onClick={() => enviar('draft')} disabled={guardar.isPending}>
              <i className="bi bi-save" aria-hidden="true" /> Guardar borrador
            </Button>
            <Button variant="secondary" onClick={() => enviar('schedule')} disabled={guardar.isPending}>
              <i className="bi bi-clock" aria-hidden="true" /> Programar
            </Button>
            <Button onClick={() => enviar('publish')} disabled={guardar.isPending}>
              <i className="bi bi-send" aria-hidden="true" />
              {guardar.isPending ? 'Guardando…' : 'Publicar'}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
        )
      }
    >
      <div className="tabs mb-3" role="tablist">
        {([['crear', 'bi-plus-circle', 'Crear'],
           ['lista', 'bi-collection', 'Borradores y programados']] as const).map(([t, icono, etiqueta]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={pestana === t}
            className={`tab-btn${pestana === t ? ' active' : ''}`}
            onClick={() => setPestana(t)}
          >
            <i className={`bi ${icono}`} aria-hidden="true" /> {etiqueta}
          </button>
        ))}
      </div>

      {pestana === 'crear' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Field
              label="Colaborador"
              ayuda="Busca un usuario del maestro, o escribe el nombre si aún no existe."
              className="mb-3"
            >
              <BuscadorDePersonas
                id="ni-persona"
                etiqueta="Buscar al colaborador por su nombre"
                marcador="Nombre completo"
                elegida={persona}
                alElegir={setPersona}
                alSoltar={() => { setPersona(null); setCampos((c) => ({ ...c, full_name: '' })); }}
              />
            </Field>

            {!persona && (
              <Field label="Nombre completo" className="mb-3">
                <Input value={campos.full_name} onChange={poner('full_name')} />
              </Field>
            )}
            {persona && (
              <p className="mb-3 text-2 text-ok-ink">
                <i className="bi bi-link-45deg" aria-hidden="true" /> Datos tomados del maestro de
                colaboradores.
              </p>
            )}

            <div className="mb-3 flex flex-wrap gap-3">
              <Field label="Cargo" className="min-w-[10rem] flex-1">
                <Input value={campos.position} onChange={poner('position')} disabled={bloqueados} />
              </Field>
              <Field label="Área" opcional className="min-w-[10rem] flex-1">
                <Input value={campos.area} onChange={poner('area')} disabled={bloqueados} />
              </Field>
            </div>
            <div className="mb-3 flex flex-wrap gap-3">
              <Field label="Fecha de ingreso" className="min-w-[10rem] flex-1">
                <Input type="date" value={campos.hire_date} onChange={poner('hire_date')} disabled={bloqueados} />
              </Field>
              <Field label="Sede" opcional className="min-w-[10rem] flex-1">
                <Input value={campos.sede} onChange={poner('sede')} disabled={bloqueados} />
              </Field>
            </div>
            <div className="mb-3 flex flex-wrap gap-3">
              <Field label="Líder inmediato" opcional className="min-w-[10rem] flex-1">
                <Input value={campos.leader} onChange={poner('leader')} />
              </Field>
              <Field label="Correo corporativo" opcional className="min-w-[10rem] flex-1">
                <Input type="email" value={campos.email} onChange={poner('email')} disabled={bloqueados} />
              </Field>
            </div>

            <Field
              label="Foto del colaborador"
              opcional
              ayuda={
                subirFoto.isPending ? 'Subiendo…'
                : fotoClave ? 'Foto cargada.'
                : subirFoto.isError ? 'No se pudo subir. Vuelve a elegirla.'
                : undefined
              }
              error={subirFoto.isError ? 'No se pudo subir la foto.' : undefined}
              className="mb-3"
            >
              <Input type="file" accept="image/*" onChange={(e) => elegirFoto(e.target.files?.[0])} />
            </Field>

            <Field label="Mensaje de bienvenida" opcional
              ayuda="Si lo dejas vacío se usa un mensaje predefinido." className="mb-3">
              <Textarea rows={2} value={campos.message} onChange={poner('message')} />
            </Field>
            <Field label="Frase personalizada" opcional className="mb-3">
              <Input value={campos.phrase} onChange={poner('phrase')}
                placeholder="Ej: ¡El equipo te espera con energía!" />
            </Field>

            <div className="border-t border-line pt-2.5">
              <label className="mb-1 flex items-center gap-2 text-2">
                <input type="checkbox" checked={alFeed} onChange={(e) => setAlFeed(e.target.checked)}
                  className="size-4 accent-brand" />
                <span>Publicar en el feed</span>
              </label>
              <label className="mb-2 flex items-center gap-2 text-2">
                <input type="checkbox" checked={alCorreo} onChange={(e) => setAlCorreo(e.target.checked)}
                  className="size-4 accent-brand" />
                <span>Enviar por correo al colaborador</span>
              </label>
              <Field label="Programar publicación" opcional>
                <Input type="datetime-local" value={programada}
                  onChange={(e) => setProgramada(e.target.value)} />
              </Field>
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-3 font-semibold">
              Vista previa <span className="font-normal text-muted">(en tiempo real)</span>
            </p>
            <VistaPrevia campos={campos} fotoUrl={fotoUrl} />
          </div>
        </div>
      ) : (
        <ListaDeIngresos activa={pestana === 'lista'} alCrear={() => setPestana('crear')} />
      )}
    </Dialog>
  );
}

/* ── La vista previa ───────────────────────────────────────────────────── */

/** La tarjeta tal como saldrá.
 *
 *  Los cinco valores que pinta —nombre, cargo, área, sede, mensaje— los acaba de
 *  teclear quien la está creando, y en el vanilla iban a `innerHTML` sin
 *  escapar. Aquí son nodos de texto.
 *
 *  Los dos hexadecimales del degradado (`#185684` → `#27AE60`) se quedan a mano:
 *  son la identidad de ESTA tarjeta, la misma que compone el servidor al
 *  generar la imagen de verdad (`celebrations.py`). Atarlos a los tokens de la
 *  marca haría que la vista previa dejara de parecerse al resultado. */
function VistaPrevia({ campos, fotoUrl }: { campos: Borrador; fotoUrl: string | null }) {
  const nombre = campos.full_name.trim() || 'Nombre del colaborador';
  const meta = [campos.position, campos.area].map((s) => s.trim()).filter(Boolean).join(' · ');
  const pastilla = [campos.hire_date ? `Ingreso: ${campos.hire_date}` : '', campos.sede.trim()]
    .filter(Boolean).join(' · ');
  const mensaje =
    (campos.message.trim() || 'Nos alegra que formes parte de nuestro equipo.') +
    (campos.phrase.trim() ? ` — ${campos.phrase.trim()}` : '');
  const iniciales =
    (nombre.split(' ').map((p) => p[0]).slice(0, 2).join('') || '?').toUpperCase();

  return (
    <div
      className="flex aspect-square flex-col items-center justify-center overflow-hidden
                 rounded-lg p-5 text-center text-white shadow-overlay"
      style={{ background: 'linear-gradient(160deg,#185684,#27AE60)' }}
    >
      <div className="text-1 font-extrabold tracking-wider text-warn-ink">
        ¡NUEVO INGRESO! LE DAMOS LA BIENVENIDA
      </div>
      <div className="my-3">
        {fotoUrl ? (
          <img
            src={fotoUrl}
            alt=""
            className="size-24 rounded-full border-4 border-white object-cover"
          />
        ) : (
          <div className="flex size-24 items-center justify-center rounded-full border-4
                          border-white bg-black/25 text-8 font-extrabold">
            {iniciales}
          </div>
        )}
      </div>
      <div className="text-6 font-extrabold leading-tight">{nombre}</div>
      {meta && <div className="mt-0.5 text-2 opacity-90">{meta}</div>}
      {pastilla && (
        <div className="mt-2 rounded bg-accent px-3 py-0.5 text-1 font-bold text-white">
          {pastilla}
        </div>
      )}
      <div className="mt-3 max-w-[90%] text-2 italic opacity-90">{mensaje}</div>
    </div>
  );
}

/* ── Borradores y programados ──────────────────────────────────────────── */

const TONO = { draft: 'neutral', scheduled: 'warn', published: 'ok' } as const;
const DICHO = { draft: 'Borrador', scheduled: 'Programado', published: 'Publicado' } as const;

function ListaDeIngresos({ activa, alCrear }: { activa: boolean; alCrear: () => void }) {
  const { data, isLoading, isError, error, refetch } = useIngresos(activa);
  const publicar = usePublicarIngreso();
  const borrar = useBorrarIngreso();
  const [porBorrar, setPorBorrar] = useState<TarjetaDeIngreso | null>(null);

  if (isLoading) return <p className="text-3 text-muted">Cargando…</p>;
  if (isError) {
    return <EstadoError err={error} que="los nuevos ingresos" onReintentar={() => { refetch(); }} />;
  }
  const items = data?.items ?? [];
  if (!items.length) {
    return (
      <EstadoVacio
        icono="bi bi-inbox"
        titulo="Sin registros"
        descripcion="Los borradores y las publicaciones programadas aparecen aquí."
        accion={{ texto: 'Crear una tarjeta', onClick: alCrear }}
      />
    );
  }

  return (
    <>
      <ul>
        {items.map((c) => (
          <li
            key={c.id}
            className="mb-2 flex items-center gap-3 rounded-sm border border-line p-2.5"
          >
            {c.card_url ? (
              <img src={c.card_url} alt="" className="size-12 rounded-sm object-cover" />
            ) : (
              <div className="flex size-12 items-center justify-center rounded-sm bg-sunken">
                <i className="bi bi-person" aria-hidden="true" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 font-bold text-brand-ink">
                <span className="truncate">{c.full_name}</span>
                <Badge tone={TONO[c.status] ?? 'neutral'}>{DICHO[c.status] ?? c.status}</Badge>
              </div>
              <div className="text-2 text-muted">
                {[c.position, c.area, c.hire_date].filter(Boolean).join(' · ')}
                {c.scheduled_at && (
                  <> · <i className="bi bi-clock" aria-hidden="true" />{' '}
                    {new Date(c.scheduled_at).toLocaleString('es-CO')}</>
                )}
              </div>
            </div>
            {c.status !== 'published' && (
              <Button
                size="sm"
                disabled={publicar.isPending}
                onClick={() =>
                  publicar.mutate(c.id, {
                    onSuccess: () => Utils.toast('Publicado en el feed', 'success'),
                    onError: (e: unknown) =>
                      Utils.toast((e as Error)?.message || 'No se pudo publicar', 'error'),
                  })
                }
              >
                <i className="bi bi-send" aria-hidden="true" /> Publicar
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Eliminar la tarjeta de ${c.full_name}`}
              onClick={() => setPorBorrar(c)}
            >
              <i className="bi bi-trash" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        abierto={!!porBorrar}
        onCambio={(v) => { if (!v) setPorBorrar(null); }}
        titulo="Eliminar la tarjeta"
        descripcion={
          porBorrar
            ? `Se elimina la tarjeta de nuevo ingreso de ${porBorrar.full_name}.`
            : ''
        }
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!porBorrar) return;
          borrar.mutate(porBorrar.id, {
            onSuccess: () => { setPorBorrar(null); Utils.toast('Eliminado', 'success'); },
            onError: (e: unknown) =>
              Utils.toast((e as Error)?.message || 'No se pudo eliminar', 'error'),
          });
        }}
      />
    </>
  );
}
